import { useCallback, useMemo } from "react";
import { Stack, Typography } from "@altimateai/lego";
import AutoTuneJobDag from "@/pages/jobs/auto-tune/AutoTuneJobDag";
import type { TaskEdge } from "@/pages/jobs/auto-tune/AutoTuneJobDag.types";
import { buildClusterRecommendationNode } from "./clusterDag";
import type { ClusterAutoTuneRecommendation } from "./types";

interface Props {
  clusterId: string;
  clusterName: string;
  workerNodeType?: string | null;
  driverNodeType?: string | null;
  // Live worker sizing from the detail page — fallback when there's no
  // resize rec so the card shows the real count instead of "—".
  minAutoscaleWorkers?: number | null;
  maxAutoscaleWorkers?: number | null;
  workerCount?: number | null;
  recommendations: ClusterAutoTuneRecommendation[];
}

// Stable empty-edge reference — a single cluster node has no edges, and a
// fresh ``[]`` each render would needlessly re-run the dagre layout in
// ``AutoTuneJobDag`` (which memoizes on ``nodes`` / ``edges`` identity).
const NO_EDGES: TaskEdge[] = [];

// Recommended-state DAG for a single all-purpose cluster. Reuses the jobs
// ``AutoTuneJobDag`` canvas with exactly one node so the cluster drawer
// shows the same Current → Proposed card the jobs Auto Tune view does —
// here surfacing the Worker Sizing and Auto Termination deltas. The card
// is read-only: enablement is driven by the sub-toggle rows above, so no
// ``onSelect`` is wired and ``onToggle`` is a no-op.
const ClusterRecommendationDag = ({
  clusterId,
  clusterName,
  workerNodeType,
  driverNodeType,
  minAutoscaleWorkers,
  maxAutoscaleWorkers,
  workerCount,
  recommendations,
}: Props) => {
  const node = useMemo(
    () =>
      buildClusterRecommendationNode(recommendations, {
        clusterId,
        clusterName,
        workerNodeType,
        driverNodeType,
        minAutoscaleWorkers,
        maxAutoscaleWorkers,
        workerCount,
      }),
    [
      recommendations,
      clusterId,
      clusterName,
      workerNodeType,
      driverNodeType,
      minAutoscaleWorkers,
      maxAutoscaleWorkers,
      workerCount,
    ]
  );

  // Memoised so the array identity is stable across drawer re-renders
  // (e.g. each sub-toggle flip) — keeps the dagre layout from re-running.
  const nodes = useMemo(() => (node ? [node] : []), [node]);

  const noop = useCallback(() => undefined, []);

  if (!node) return null;

  return (
    <Stack direction="column" gap="sm" className="al-mt-4">
      <Typography weight="medium">Recommended State</Typography>
      <div className="al-rounded-lg al-border al-border-stroke-light al-overflow-hidden">
        {/* Bound the canvas height: a single node centers via xyflow's
            fitView, and the fixed box keeps the drawer layout stable.
            ``fitViewMaxZoom`` caps the fit so the lone card renders at a
            natural size rather than blown up to fill the canvas. */}
        <div className="al-h-[320px]">
          <AutoTuneJobDag nodes={nodes} edges={NO_EDGES} onToggle={noop} fitViewMaxZoom={1} />
        </div>
      </div>
    </Stack>
  );
};

export default ClusterRecommendationDag;
